/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { FaAngleRight } from "react-icons/fa";
import { storageService } from "services/storageService";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { MENU_ITEM_CLASS, MENU_ITEM_ICON_CLASS, MENU_ITEM_VALUE_CLASS } from "components/SideBar/menuItemStyles";

interface OrgEntry {
  subdomainName: string;
  active: string | boolean;
}

interface MyOrganizationsResponse {
  organizations: OrgEntry[];
  count: number;
}

interface SwitchTenantResponse {
  token: string;
  message: string;
  refreshToken?: string;
}

export const isOrgSwitcherVisible = () =>
  storageService.getItem("isMultiOrg") === "true";

interface OrgSwitcherProps {
  variant?: "toolbar" | "menu";
}

export default function OrgSwitcher({ variant = "toolbar" }: Readonly<OrgSwitcherProps>) {
  const { addToast } = useToaster();

  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState<OrgEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isMultiOrg = isOrgSwitcherVisible();

  const isOrgActive = (value: string | boolean) =>
    value === true || value === "true";

  const currentSubdomain =
    organizations.find((org) => isOrgActive(org.active))?.subdomainName ?? null;

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // load organizations on first open
  useEffect(() => {
    if (!open || loaded) return;

    apiService
      .get("api/auth/my-organizations")
      .then((res) => {
        const data: MyOrganizationsResponse = res.data;
        setOrganizations(data.organizations || []);
        setLoaded(true);
      })
      .catch(() => {
        addToast("Failed to load organizations.", "error");
        setOpen(false);
      });
  }, [open, loaded]);

  const handleSwitch = (subdomainName: string) => {
    if (switching || subdomainName === currentSubdomain) return;

    setSwitching(subdomainName);

    apiService
      .post(
        "api/auth/switch-tenant",
        {},
        { params: { subdomain: subdomainName } } as any
      )
      .then((res) => {
        const data: SwitchTenantResponse = res.data;

        storageService.setItem("accessToken", data.token);
        // Store in localStorage — consistent with login flow; survives browser restart and new tabs
        storageService.setItem("activeSubdomain", subdomainName);
        if (data.refreshToken) {
          storageService.setItem("refreshToken", data.refreshToken);
        }
        window.dispatchEvent(
          new CustomEvent("auth-token-updated", {
            detail: { token: data.token },
          }),
        );

        
        addToast(
          data.message || "Switched organization successfully.",
          "success"
        );
        setOrganizations((prev) =>
          prev.map((org) => ({
            ...org,
            active: org.subdomainName === subdomainName,
          })),
        );
        setOpen(false);
        setSwitching(null);
      })
      .catch(() => {
        addToast("Failed to switch organization.", "error");
        setSwitching(null);
      });
  };

  const getInitials = (name: string) =>
    name
      .split(/[\s-_]/)
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  if (!isMultiOrg) return null;

  const isMenu = variant === "menu";

  const selectedOrgName =
    currentSubdomain ?? storageService.getItem("activeSubdomain") ?? "";

  const triggerClass = isMenu
    ? MENU_ITEM_CLASS
    : "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700 shadow-sm";

  const panelPositionClass = isMenu ? "left-full top-0 ml-1" : "right-0 mt-2";

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        title={isMenu ? undefined : "Switch Organization"}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerClass}
      >
        {isMenu ? (
          <>
            <HiOutlineOfficeBuilding className={MENU_ITEM_ICON_CLASS} aria-hidden="true" />
            <span>Organization</span>
            <span className={MENU_ITEM_VALUE_CLASS}>
              <span className="max-w-24 truncate capitalize">{selectedOrgName}</span>
              <FaAngleRight
                className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </span>
          </>
        ) : (
          <>
            <HiOutlineOfficeBuilding className="w-5 h-5 text-slate-600 dark:text-slate-300" />

            <span className="hidden sm:inline max-w-[130px] truncate capitalize">
              {currentSubdomain ?? "Organization"}
            </span>

            <svg
              className={`w-4 h-4 transition-transform text-slate-500 dark:text-slate-300 ${open ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`absolute ${panelPositionClass} w-72 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden`}>
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300">
              Organizations
            </p>
          </div>

          {/* Loader */}
          {!loaded ? (
            <div className="flex items-center justify-center py-8">
              <svg
                className="animate-spin w-5 h-5 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            </div>
          ) : (
            <ul className="py-1 max-h-64 overflow-y-auto">
              {organizations.map((org) => {
                const isCurrent =
                  org.subdomainName === currentSubdomain;

                const isSwitching =
                  switching === org.subdomainName;

                return (
                  <li key={org.subdomainName}>
                    <button
                      onClick={() =>
                        handleSwitch(org.subdomainName)
                      }
                      disabled={isCurrent || !!switching}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isCurrent
                          ? "bg-blue-50 dark:bg-blue-900/20 cursor-default"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      {/* Avatar */}
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isCurrent
                            ? "bg-blue-100 dark:bg-blue-800"
                            : "bg-gray-100 dark:bg-gray-700"
                        }`}
                      >
                        <span
                          className={`text-xs font-semibold ${
                            isCurrent
                              ? "text-blue-700 dark:text-blue-200"
                              : "text-gray-600 dark:text-gray-300"
                          }`}
                        >
                          {getInitials(org.subdomainName)}
                        </span>
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate capitalize text-gray-900 dark:text-white">
                          {org.subdomainName}
                        </p>

                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {org.subdomainName}.liriworks.com
                        </p>
                      </div>

                      {/* Active Check */}
                      {/*  Show checkmark if this is the current organizati */}
                      {isCurrent && (
                        <svg
                          className="w-4 h-4 text-blue-500 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          />
                        </svg>
                      )}

                      {/* Switching Loader */}
                      {isSwitching && (
                        <svg
                          className="animate-spin w-4 h-4 text-blue-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}